import type { SharedEmailProvider, SharedEmailProviders } from '@core/email-providers-types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
} from '@core/ngrx-legacy';
import { generateUuid } from '@core/utils-legacy';

import { EntityStoreKey } from '../types';

const { actions, selectors, reducer, namespace } =
  createEntityCollectionStore<SharedEmailProvider>()(EntityStoreKey.sharedEmailProviders, {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const {
        createOnePessimistic,
        updateOnePessimistic,
        updateOneOptimistic,
        updateOneOptimisticSuccess,
        updateOneOptimisticFailed,
        updateOnePessimisticSuccess,
        updateOnePessimisticFailed,
        removeOneOptimistic,
        removeOneOptimisticSuccess,
        removeOneOptimisticFailed,
      } = getEntityCollectionActions(adapter, entity => entity.id);

      return {
        archiveWithAutomatedActionMigration: ngrxEvent<{
          from: number;
          to: { type: string; id: number | null };
        }>(),
        archiveWithAutomatedActionMigrationSuccess: ngrxEvent<SharedEmailProvider>(),
        archiveWithAutomatedActionMigrationFailed: ngrxEvent<any>(),
        archiveWithAutomationActionMigration: ngrxEvent<{
          from: number;
          to: { type: string; id: number | null };
        }>(),
        archiveWithAutomationActionMigrationSuccess: ngrxEvent<SharedEmailProvider>(),
        archiveWithAutomationActionMigrationFailed: ngrxEvent<any>(),
        archiveOneOptimistic: (
          state,
          payload: { data: SharedEmailProvider; correlationId: string },
        ) =>
          payload.data.connectionStatus !== 'initial'
            ? updateOneOptimistic(state, {
                data: { ...payload.data, archivedAt: 'now' },
                correlationId: payload.correlationId,
              })
            : removeOneOptimistic(state, {
                data: payload.data.id,
              }),
        archiveOneOptimisticSuccess: (
          state,
          payload: { data: SharedEmailProvider; correlationId: string },
        ) =>
          payload.data.connectionStatus !== 'initial'
            ? updateOneOptimisticSuccess(state, {
                data: payload.data,
                correlationId: payload.correlationId,
              })
            : removeOneOptimisticSuccess(state, {
                data: payload.data,
              }),
        archiveOneOptimisticFailed: (
          state,
          payload: { data: any; error: any; correlationId: string },
        ) =>
          payload.data.connectionStatus !== 'initial'
            ? updateOneOptimisticFailed(state, {
                data: {},
                correlationId: payload.correlationId,
                scope: 'main',
                error: <any>null,
              })
            : removeOneOptimisticFailed(state, {
                data: payload.data.id,
                error: <any>null,
              }),
        migrateAutomatedActions: (
          state,
          payload: { from: number; to: { type: string; id: number | null } },
        ) => adapter.markAsPending(state, { scope: 'main', entities: [payload.from] }),
        migrateAutomatedActionsSuccess: (state, payload: SharedEmailProvider) =>
          updateOnePessimisticSuccess(state, {
            data: payload,
          }),
        migrateAutomatedActionsFailed: (state, payload: SharedEmailProvider) =>
          updateOnePessimisticFailed(state, {
            data: payload,
            error: <any>undefined,
          }),
        migrateAutomationActions: (
          state,
          payload: { from: number; to: { type: string; id: number | null } },
        ) => adapter.markAsPending(state, { scope: 'main', entities: [payload.from] }),
        migrateAutomationActionsSuccess: (state, payload: SharedEmailProvider) =>
          updateOnePessimisticSuccess(state, {
            data: payload,
          }),
        migrateAutomationActionsFailed: (state, payload: SharedEmailProvider) =>
          updateOnePessimisticFailed(state, {
            data: payload,
            error: <any>undefined,
          }),
        createOnePessimistic: (state, payload: SharedEmailProviders) =>
          createOnePessimistic(state, {
            correlationId: generateUuid(),
            data: {
              provider: payload,
            },
          }),
        connectSharedMailbox: (state, payload: SharedEmailProvider) =>
          updateOnePessimistic(state, {
            data: payload,
          }),
      };
    },
  });

const SharedEmailProvidersActions: Pick<
  typeof actions,
  | 'fetchCollection'
  | 'fetchCollectionSuccess'
  | 'fetchCollectionFailed'
  | 'archiveWithAutomatedActionMigration'
  | 'archiveWithAutomatedActionMigrationSuccess'
  | 'archiveWithAutomatedActionMigrationFailed'
  | 'archiveWithAutomationActionMigration'
  | 'archiveWithAutomationActionMigrationSuccess'
  | 'archiveWithAutomationActionMigrationFailed'
  | 'archiveOneOptimistic'
  | 'archiveOneOptimisticSuccess'
  | 'archiveOneOptimisticFailed'
  | 'migrateAutomatedActions'
  | 'migrateAutomatedActionsSuccess'
  | 'migrateAutomatedActionsFailed'
  | 'migrateAutomationActions'
  | 'migrateAutomationActionsSuccess'
  | 'migrateAutomationActionsFailed'
  | 'createOnePessimistic'
  | 'createOnePessimisticSuccess'
  | 'createOnePessimisticFailed'
  | 'updateOnePessimistic'
  | 'updateOnePessimisticSuccess'
  | 'updateOnePessimisticFailed'
  | 'removeOneOptimistic'
  | 'removeOneOptimisticSuccess'
  | 'removeOneOptimisticFailed'
  | 'connectSharedMailbox'
> = actions;

export {
  SharedEmailProvidersActions,
  selectors as SharedEmailProvidersSelectors,
  reducer as SharedEmailProvidersReducer,
  namespace as SharedEmailProvidersNamespace,
};
